import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import {
  ArrowRight,
  BarChart2,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Zap,
} from './icons';
import { usePageMeta } from '../../lib/usePageMeta';
import { REPORT_PRICE } from '../../lib/pricing';
import { routes } from '../../lib/assessment/routes';
import { buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * The Bottleneck marketing page, migrated from the legacy application's
 * `src/pages/bottleneck.tsx` (V2-018C-2).
 *
 * Parity migration. Copy, headings, section order, the six types, the eight
 * categories, both inclusion lists, the workbook preview and its eight
 * sections, the FAQ, price references, and the structured data are reproduced
 * exactly. The same four categories of change apply as on the previous pages,
 * and only those four: V2 routing, the Experience Shell owning the header and
 * footer, obsolete-technology removal (Clerk), and the accessibility flattening
 * of controls nested inside anchors.
 *
 * The FAQ accordion keeps its native `<button>` and its open/closed state, so
 * the interaction is the legacy interaction. It gains `aria-expanded` and
 * `aria-controls`, which the legacy markup omits — an accessibility change, not
 * a behavioural one.
 */
const TYPES = [
  {
    name: 'The Overextended High Performer',
    desc: 'Capacity stretched beyond what the system can sustain. Output is high but consistency is slipping.',
  },
  {
    name: 'The Avoidant High Performer',
    desc: 'Real skill and awareness present. Avoidance is blocking consistent follow-through on what matters most.',
  },
  {
    name: 'The Identity-Pressured High Performer',
    desc: 'Identity pressure is limiting real risk, honest action, and clear decision-making.',
  },
  {
    name: 'The Scattered Builder',
    desc: 'More capacity and ideas than focused execution bandwidth. Starting too much, finishing too little.',
  },
  {
    name: 'The Isolated Operator',
    desc: 'Self-reliance has become the performance ceiling. Carrying too much alone.',
  },
  {
    name: 'The Under-Recovered High Performer',
    desc: 'A chronic recovery gap that limits clarity, consistency, and output more than effort ever could.',
  },
];

const CATEGORIES = [
  { name: 'Avoidance', desc: 'Tendency to delay or sidestep high-stakes tasks' },
  { name: 'Overextension', desc: 'Capacity stretch beyond sustainable output' },
  { name: 'Identity Pressure', desc: 'Performance tied to self-image and external validation' },
  { name: 'Focus', desc: 'Ability to direct attention to the highest-priority work' },
  { name: 'Recovery', desc: 'Consistency of rest, reset, and recharge behaviors' },
  { name: 'Emotional Regulation', desc: 'Ability to act from intention rather than reaction' },
  { name: 'Goal Clarity', desc: 'Sharpness of direction and commitment to priorities' },
  { name: 'Isolation', desc: 'Degree to which performance relies on going it alone' },
];

const FREE_INCLUDES = [
  'Your primary bottleneck type',
  '4 performance scores (Bottleneck, Execution Readiness, Recovery Deficit, Clarity)',
  'Your top 2 bottleneck categories',
  'Overall HP Score and initial action guidance',
];

const PAID_INCLUDES = [
  'Full bottleneck profile and secondary pattern',
  'Complete category breakdown across all 8 dimensions',
  'Deep dive: why this pattern developed, the hidden cost, and the false belief beneath it',
  'Stop Doing and Start Doing. 8 behavior-level changes specific to your type.',
  '7-Day Reset Journal with daily objectives, coaching notes, and prompts',
  '30-Day Reset Workbook with weekly goals, behaviors, and progress trackers',
  'Downloadable PDF',
];

const WORKBOOK_SECTIONS = [
  { title: 'Cover Page', body: 'Your type, date, and report overview.' },
  {
    title: 'How to Use This Workbook',
    body: 'Step-by-step instructions for using the journal and workbook sections effectively.',
  },
  {
    title: 'Deep Dive',
    body: 'Why this pattern developed, the hidden cost, the false belief keeping it in place, and what changes when you address it.',
  },
  {
    title: 'Stop Doing and Start Doing',
    body: '8 specific behaviors to stop and 8 to start, tailored to your type.',
  },
  {
    title: '7-Day Reset Journal',
    body: '7 daily pages with an objective, coaching note, action steps, journal prompts, and a daily reflection section with writing lines.',
  },
  {
    title: '30-Day Reset Workbook',
    body: '4 weekly pages with goals, key behaviors, journal prompts, and daily progress trackers.',
  },
  {
    title: '30-Day Progress Review',
    body: 'A completion tracker and reflection prompts to capture what changed.',
  },
  {
    title: 'Your Next Step',
    body: 'A final page with a 5-step post-workbook plan and reassessment CTA.',
  },
];

const FAQ_ITEMS = [
  {
    q: 'What is the Bottleneck Assessment?',
    a: 'It identifies the internal operating pattern that is creating the most friction in your performance. The assessment uses 50 questions across 8 categories to surface your primary bottleneck type, performance scores, and top friction areas.',
  },
  {
    q: 'Who is the Bottleneck Assessment for?',
    a: 'It is for high performers who are producing results but experiencing friction: inconsistency, cognitive overload, difficulty following through, or a sense that effort is outpacing output.',
  },
  {
    q: 'What does the assessment measure?',
    a: 'It measures 8 performance categories: Avoidance, Overextension, Identity Pressure, Focus, Recovery, Emotional Regulation, Goal Clarity, and Isolation. Your highest-scoring category determines your bottleneck type.',
  },
  {
    q: 'What do I get for free?',
    a: 'Your free result includes your primary bottleneck type, 4 performance scores (Bottleneck, Execution Readiness, Recovery Deficit, Clarity), your top 2 bottleneck categories, and your overall High Performer Score.',
  },
  {
    q: 'What is in the full report?',
    a: 'The full report includes your complete bottleneck profile and secondary pattern, a deep dive into why the pattern developed, Stop Doing and Start Doing behavior lists specific to your type, a 7-Day Reset Journal, a 30-Day Reset Workbook, and a downloadable PDF.',
  },
  {
    q: 'Is this a clinical or mental health tool?',
    a: 'No. The Bottleneck Assessment is for educational and coaching purposes only. It does not diagnose or treat any condition and is not a substitute for medical or mental health care.',
  },
];

export function Bottleneck() {
  usePageMeta({
    title: 'High Performer Bottleneck Assessment | High Performer OS',
    description:
      'Take the free 50-question High Performer Bottleneck Assessment to identify the internal pattern limiting your clarity, consistency, recovery, and execution.',
    canonical: 'https://highperformeros.com/bottleneck',
    ogImage: 'https://highperformeros.com/logo-white-v2.webp',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-bottleneck';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-bottleneck');
      if (el) document.head.removeChild(el);
    };
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-4 py-24 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-full mb-8">
          50-Question Assessment · 6 High Performer Types · Free Results
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
          You can be capable <span className="text-primary">and still feel stuck.</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          The Bottleneck Assessment identifies the internal pattern creating drag in your
          performance. Capability is often not the issue. The pattern is.
        </p>
        <Link
          href={routes.assessment('bottleneck')}
          className={cn(
            buttonVariants({ size: 'lg' }),
            'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)]',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        >
          Take the Free Bottleneck Assessment
        </Link>
        <p className="text-xs text-muted-foreground mt-4">
          No credit card required. Free results included.
        </p>
      </section>

      {/* AISEO Answer Block */}
      <section className="py-16 px-4 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is the Bottleneck Assessment?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Bottleneck Assessment is a 50-question diagnostic that identifies the internal
              operating pattern creating the most friction in your performance. It surfaces your
              primary bottleneck type and four performance scores across Bottleneck, Execution
              Readiness, Recovery Deficit, and Clarity. The result is free.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              Who is it for?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It is for high performers who are producing results but experiencing friction.
              Inconsistency, cognitive overload, difficulty following through, or a persistent sense
              that effort is outpacing output are all signals that a bottleneck pattern is active.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What does it measure?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It measures 8 performance categories: Avoidance, Overextension, Identity Pressure,
              Focus, Recovery, Emotional Regulation, Goal Clarity, and Isolation. Your
              highest-scoring category determines your bottleneck type.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What do you get for free?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your free result includes your primary bottleneck type, 4 performance scores, your top
              2 bottleneck categories, and your overall High Performer Score. No payment required.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is included in the full report?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The full report includes your complete bottleneck profile and secondary pattern, a
              deep dive into why the pattern developed, Stop Doing and Start Doing behavior lists
              specific to your type, a 7-Day Reset Journal, a 30-Day Reset Workbook, and a
              downloadable PDF. It is a one-time purchase unlocked after your free result.
            </p>
          </div>
        </div>
      </section>

      {/* Free result or full report? */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              Compare
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Free result or full report?</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto">
              Start with the free result. Unlock the full report when you want the deeper pattern
              breakdown and a concrete plan for sustained change.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-background p-7 space-y-5">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Free
                </span>
                <h3 className="text-xl font-bold mt-1">Assessment Results</h3>
              </div>
              <ul className="space-y-3">
                {FREE_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href={routes.assessment('bottleneck')}
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'w-full mt-2',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                Start Free Assessment
              </Link>
            </div>

            <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-7 space-y-5 relative">
              <div className="absolute -top-3 left-6">
                <span className="text-xs font-bold uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1 rounded-full">
                  {`Full Report · $${REPORT_PRICE}`}
                </span>
              </div>
              <div className="pt-2">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  One-time payment
                </span>
                <h3 className="text-xl font-bold mt-1">Full Report and 30-Day Workbook</h3>
              </div>
              <ul className="space-y-3">
                {PAID_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Unlocked after taking the free assessment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">How it works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: <BarChart2 className="w-5 h-5" />,
                step: '01',
                title: 'Take the 50-question assessment',
                desc: 'About 7 minutes. Rate yourself on 50 statements across 8 performance categories.',
              },
              {
                icon: <ChevronRight className="w-5 h-5" />,
                step: '02',
                title: 'Get your free results',
                desc: 'See your High Performer Type, 4 performance scores, and top bottleneck categories.',
              },
              {
                icon: <FileText className="w-5 h-5" />,
                step: '03',
                title: 'Unlock the full report',
                desc: `One-time $${REPORT_PRICE} payment. Immediate access to your full personalized report.`,
              },
              {
                icon: <Calendar className="w-5 h-5" />,
                step: '04',
                title: 'Follow the 30-day plan',
                desc: 'Work through your 7-day reset journal, then your 30-day workbook. Reassess after 30 days.',
              },
            ].map((s) => (
              <div key={s.step} className="text-center space-y-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto text-primary">
                  {s.icon}
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {s.step}
                </div>
                <h4 className="font-bold text-sm">{s.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What it measures */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            What the assessment measures
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            50 questions across 8 performance categories. Your highest-scoring category determines
            your bottleneck type.
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {CATEGORIES.map((c) => (
              <div key={c.name} className="p-5 rounded-xl border border-border bg-background space-y-2">
                <BarChart2 className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-sm">{c.name}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 6 Types */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            The 6 High Performer Types
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            Your type is determined by your highest-scoring bottleneck pattern, not your strengths.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {TYPES.map((t) => (
              <div key={t.name} className="rounded-xl border border-border bg-card p-5 space-y-2">
                <Zap className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-sm">{t.name}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workbook Preview */}
      <section className="bg-card border-y border-border py-20 px-4 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-16">
            <div className="relative" style={{ width: 320, height: 360 }} aria-hidden="true">
              {/* Page 3 — Cover (back) */}
              <div
                style={{
                  position: 'absolute',
                  width: 220,
                  height: 290,
                  background: '#0d1526',
                  borderRadius: 10,
                  padding: '22px 20px',
                  transform: 'rotate(-9deg) translateX(-10px)',
                  top: 30,
                  left: 0,
                  boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
                  zIndex: 1,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontSize: 7,
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: '#c9a84c',
                    marginBottom: 48,
                  }}
                >
                  High Performer OS
                </div>
                <div
                  style={{
                    fontSize: 8,
                    fontWeight: 600,
                    color: '#8b9bb4',
                    marginBottom: 10,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  Full Performance Report
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: '#ffffff',
                    lineHeight: 1.15,
                    marginBottom: 10,
                  }}
                >
                  The Depleted Driver
                </div>
                <div style={{ fontSize: 7.5, color: '#8b9bb4', lineHeight: 1.7, marginBottom: 20 }}>
                  Driven and results-oriented. Running on empty, using will to maintain output.
                </div>
                <div style={{ width: 28, height: 2, background: '#c9a84c', marginBottom: 14 }} />
                <div style={{ fontSize: 7, color: '#8b9bb4', lineHeight: 2.2 }}>
                  <div>
                    <strong style={{ color: '#fff' }}>Completed</strong>&nbsp;&nbsp;June 2026
                  </div>
                  <div>
                    <strong style={{ color: '#fff' }}>Report Type</strong>&nbsp;&nbsp;Full Paid
                    Report
                  </div>
                </div>
              </div>

              {/* Page 2 — Deep Dive (middle) */}
              <div
                style={{
                  position: 'absolute',
                  width: 220,
                  height: 290,
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: '18px 18px',
                  transform: 'rotate(-3deg)',
                  top: 15,
                  left: 42,
                  boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
                  zIndex: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontSize: 7,
                    fontWeight: 700,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#c9a84c',
                    marginBottom: 3,
                  }}
                >
                  Section 01
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: '#0d1526',
                    marginBottom: 10,
                    paddingBottom: 8,
                    borderBottom: '2px solid #f0ede8',
                  }}
                >
                  Deep Dive
                </div>
                <div
                  style={{
                    background: '#f8f6f2',
                    border: '1px solid #e5e0d8',
                    borderRadius: 6,
                    padding: '9px 11px',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 7,
                      fontWeight: 700,
                      color: '#8b9bb4',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    What This Pattern Looks Like
                  </div>
                  {[82, 96, 68, 88, 74].map((w, i) => (
                    <div
                      key={i}
                      style={{
                        height: 5,
                        background: '#d8d4cc',
                        borderRadius: 2,
                        width: `${w}%`,
                        marginBottom: 4,
                      }}
                    />
                  ))}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    marginBottom: 8,
                  }}
                >
                  {['Why This Develops', 'The Hidden Cost'].map((label) => (
                    <div
                      key={label}
                      style={{
                        background: '#faf7f0',
                        border: '1px solid #e8dfc8',
                        borderRadius: 6,
                        padding: '8px 9px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 6.5,
                          fontWeight: 700,
                          color: '#8b9bb4',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          marginBottom: 5,
                        }}
                      >
                        {label}
                      </div>
                      {[88, 72, 92, 65].map((w, i) => (
                        <div
                          key={i}
                          style={{
                            height: 4,
                            background: '#ddd8cc',
                            borderRadius: 2,
                            width: `${w}%`,
                            marginBottom: 3,
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    borderLeft: '3px solid #c9a84c',
                    background: '#faf7f0',
                    padding: '9px 11px',
                    borderRadius: '0 6px 6px 0',
                  }}
                >
                  <div
                    style={{
                      fontSize: 7,
                      fontWeight: 700,
                      color: '#c9a84c',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}
                  >
                    The Truth You Need to Hear
                  </div>
                  {[90, 75, 55].map((w, i) => (
                    <div
                      key={i}
                      style={{
                        height: 4,
                        background: '#ddd8cc',
                        borderRadius: 2,
                        width: `${w}%`,
                        marginBottom: 3,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Page 1 — 7-Day Reset (front) */}
              <div
                style={{
                  position: 'absolute',
                  width: 220,
                  height: 290,
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: '18px 18px',
                  transform: 'rotate(4deg)',
                  top: 0,
                  left: 85,
                  boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
                  zIndex: 3,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontSize: 7,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#c9a84c',
                    marginBottom: 8,
                  }}
                >
                  Section 04 · 7-Day Reset Journal
                </div>
                <div
                  style={{
                    background: '#0d1526',
                    borderRadius: 6,
                    padding: '9px 12px',
                    marginBottom: 10,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 7,
                      fontWeight: 700,
                      color: '#c9a84c',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      flexShrink: 0,
                    }}
                  >
                    Day 1
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#ffffff' }}>
                    Identify the Pattern
                  </span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 7,
                      color: '#8b9bb4',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      marginBottom: 2,
                    }}
                  >
                    Objective
                  </div>
                  <div
                    style={{ fontSize: 8, fontWeight: 600, color: '#0d1526', marginBottom: 7 }}
                  >
                    Name what is actually happening.
                  </div>
                  <div
                    style={{
                      background: '#f0ede8',
                      borderRadius: 4,
                      padding: '7px 9px',
                      fontSize: 7.5,
                      color: '#4a4a4a',
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                    }}
                  >
                    Your pattern is not a flaw. It is a strategy that worked until it stopped
                    working.
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 7,
                      color: '#8b9bb4',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Today's Action Steps
                  </div>
                  {[
                    'Review your Deep Dive section',
                    'Write in the journal prompts',
                    'Set one boundary today',
                  ].map((a, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 7.5,
                        color: '#2d2d2d',
                        marginBottom: 4,
                        display: 'flex',
                        gap: 6,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span style={{ color: '#c9a84c', fontWeight: 700, flexShrink: 0 }}>›</span>
                      {a}
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid #e5e0d8', paddingTop: 8 }}>
                  <div
                    style={{
                      fontSize: 7,
                      color: '#8b9bb4',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Journal Prompts
                  </div>
                  {[0, 1, 2].map((i) => (
                    <div key={i} style={{ borderBottom: '1px solid #e5e0d8', height: 18 }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="text-center space-y-4 mb-10">
            <h2 className="text-2xl md:text-3xl font-bold">
              A workbook built to be used, not filed away
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              After unlocking, download your full report as a print-ready PDF workbook, designed to
              be written in, returned to, and completed over 30 days.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-left">
            {WORKBOOK_SECTIONS.map((s) => (
              <div
                key={s.title}
                className="p-5 rounded-xl border border-border bg-background space-y-1.5"
              >
                <h4 className="font-bold text-sm">{s.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-3" data-testid="faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-panel-${i}`}
                  data-testid={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold hover:bg-card transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 ml-4 text-muted-foreground transition-transform duration-150 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div
                    id={`faq-panel-${i}`}
                    className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold">
            Find the pattern that is creating drag.
          </h2>
          <p className="text-muted-foreground">
            {`Take the free 50-question assessment. Get your type, scores, and top bottleneck categories. Unlock the full report for $${REPORT_PRICE}.`}
          </p>
          <Link
            href={routes.assessment('bottleneck')}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Take the Free Bottleneck Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-sm text-muted-foreground">
            Looking for Work Fit instead?{' '}
            <Link
              href={routes.workFit}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Work Fit Assessment.
            </Link>
          </p>
          <p className="text-sm text-muted-foreground">
            Looking for Perspective instead?{' '}
            <Link
              href={routes.perspective}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Perspective Assessment.
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}

export { FAQ_ITEMS as BOTTLENECK_FAQ_ITEMS, TYPES, CATEGORIES, WORKBOOK_SECTIONS };
